import { prefixHenSitePath } from '@highereducation/request-context';
import type { RankingSchool } from './types';

interface RangoSchoolRaw {
  rank: number;
  blurb?: string;
  schoolId?: string;
  overrideFields?: {
    city?: string;
    stateName?: string;
    schoolName?: string;
    superlativeTitle?: string | null;
  };
  details?: Record<string, unknown>;
  helid?: string;
  url?: string;
  [key: string]: unknown;
}

interface RangoResponse {
  extId?: string;
  gid?: string;
  schools?: RangoSchoolRaw[];
  rankings?: RangoSchoolRaw[];
  isLegacy?: boolean;
  /**
   * Raw upstream value. Treated as `unknown` because the response comes
   * from an untrusted network payload — the literal union would collapse
   * to `string` at the type level and offer no real narrowing. Use
   * `narrowRankingType` to coerce into the supported variant union.
   */
  type?: unknown;
  [key: string]: unknown;
}

export interface RangoResult {
  schools: RankingSchool[];
  isLegacy: boolean;
  type: 'ranked' | 'unranked';
}

function toNumber(val: unknown): number | undefined {
  if (val == null) return undefined;
  const n = Number(val);
  return Number.isFinite(n) ? n : undefined;
}

/** Rango returns percentages on a 0-100 scale; RankingSchool expects 0-1 ratios. */
function toRatio(val: unknown): number | undefined {
  const n = toNumber(val);
  if (n == null) return undefined;
  return n > 1 ? n / 100 : n;
}

function toBool(val: unknown): boolean | undefined {
  if (val == null) return undefined;
  if (typeof val === 'string')
    return val.toLowerCase() === 'true' || val === '1';
  return Boolean(val);
}

/**
 * Narrows the Rango `type` field to the supported variant union. Only the
 * exact lowercase string `'unranked'` flips behavior; anything else
 * (undefined, unrecognized strings, casing variants, non-string types)
 * falls back to `'ranked'` so payloads without an explicit opt-out keep
 * rendering with `#N` badges.
 */
function narrowRankingType(value: unknown): 'ranked' | 'unranked' {
  return value === 'unranked' ? 'unranked' : 'ranked';
}

function transformSchool(raw: RangoSchoolRaw, index: number): RankingSchool {
  const d = (raw.details ?? {}) as Record<string, unknown>;
  const sdbSchoolId =
    (raw.schoolId as string | undefined) ??
    (d['sdbSchoolId'] as string | undefined) ??
    (d['schoolId'] as string | undefined);
  return {
    rank: raw.rank ?? index + 1,
    name: (raw.overrideFields?.schoolName ?? d['schoolName'] ?? '') as string,
    sdbSchoolId,
    url: raw.url ?? undefined,
    helid: raw.helid ?? undefined,
    city: (raw.overrideFields?.city ?? d['city'] ?? undefined) as
      | string
      | undefined,
    stateCode: (d['stateCode'] ?? undefined) as string | undefined,
    blurb: (raw.blurb ?? undefined) as string | undefined,
    schoolLevel: d['schoolLevel'] as string | undefined,
    // Kept on the raw 0-100 scale (not a ratio): getSchoolType buckets it by
    // the 0 / 100 boundaries and it is never rendered as a percentage.
    percentOnlinePrograms: toNumber(d['percentOnlinePrograms']),
    accreditation: d['accreditation'] as string | undefined,
    superlativeTitle: (raw.overrideFields?.superlativeTitle ?? undefined) as
      | string
      | undefined,
    undergradTuitionInState: toNumber(d['undergradTuitionInState']),
    undergradTuitionOutOfState: toNumber(d['undergradTuitionOutOfState']),
    gradTuitionInState: toNumber(d['gradTuitionInState']),
    gradTuitionOutOfState: toNumber(d['gradTuitionOutOfState']),
    percentOnlineEnrollment: toRatio(d['percentOnlineEnrollment']),
    retentionRate: toRatio(d['retentionRate']),
    admissionRate: toRatio(d['admissionRate']),
    graduationRate: toRatio(d['graduationRate']),
    totalStudentsEnrolled: toNumber(d['totalStudentsEnrolled']),
    isPrivate: toBool(d['isPrivate']),
    overallScore: toRatio(d['overallScore']),
    academicQuality: toRatio(d['academicQuality']),
    recommend: toRatio(d['recommend']),
    wouldAttendAgain: toRatio(d['wouldAttendAgain']),
  };
}

export async function fetchRangoData(id: string): Promise<RangoResult | null> {
  if (!id) return null;

  try {
    const isClient = typeof window !== 'undefined';
    const url = isClient
      ? prefixHenSitePath(`/api/rango/${encodeURIComponent(id)}`)
      : `https://rankings.rango.rveducation.io/${encodeURIComponent(id)}.json`;

    const res = await fetch(url);
    if (res.status !== 200) return null;

    const data = (await res.json()) as RangoResponse;
    if (!data) return null;

    const isLegacy = !data.extId;
    const rawSchools: RangoSchoolRaw[] = isLegacy
      ? ((data.rankings as RangoSchoolRaw[]) ?? [])
      : ((data.schools as RangoSchoolRaw[]) ?? []);

    const schools = rawSchools.map((s, i) => {
      if (!isLegacy) return transformSchool(s, i);

      // Legacy Rango responses use PascalCase field names instead of the
      // modern camelCase shape. Normalize them before transforming.
      const normalized: RangoSchoolRaw = {
        ...s,
        rank:
          s.rank ?? toNumber((s as Record<string, unknown>)['Rank']) ?? i + 1,
        blurb:
          s.blurb ??
          ((s as Record<string, unknown>)['Copy'] as string | undefined),
        overrideFields: {
          ...s.overrideFields,
          schoolName:
            s.overrideFields?.schoolName ??
            ((s as Record<string, unknown>)['School'] as string | undefined),
          city:
            s.overrideFields?.city ??
            ((s as Record<string, unknown>)['City '] as string | undefined) ??
            ((s as Record<string, unknown>)['City'] as string | undefined),
          stateName:
            s.overrideFields?.stateName ??
            ((s as Record<string, unknown>)['State'] as string | undefined),
        },
        helid:
          s.helid ??
          ((s as Record<string, unknown>)['Helid'] as string | undefined),
        url:
          s.url ??
          ((s as Record<string, unknown>)['Url'] as string | undefined),
      };

      return transformSchool(normalized, i);
    });
    return { schools, isLegacy, type: narrowRankingType(data.type) };
  } catch {
    return null;
  }
}
